import React, { useContext, useEffect, useState } from 'react'
import { ModalActions } from '../common/modal/Modal'
import { IListState } from '../../interfaces/IListState'
import { t } from '../../i18n/i18n'
import { GridItem } from '../common/grid/GridItem'
import { Grid } from '../common/grid/Grid'
import { Button, makeStyles, tokens, shorthands, ProgressBar } from '@fluentui/react-components'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { useForm } from 'react-hook-form'
import { DataClassification, RiskAnalysis, Workplace } from '../../api/schemas/schema'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { enumValuesToOptions, getOptionsFromEnumValues } from '../../helpers/enumHelper'
import { DataContext } from '../../context/DataContext'
import { addRiskAnalysis } from '../../helpers/riskManagementHelper'
import { Text } from '../common/text/Text'
import { useNavigate } from 'react-router-dom'
import { ControlledDropDown } from '../common/inputs/ControlledDropdown'
import { ControlledWorkplaceDropdownMulti } from '../common/inputs/ControlledWorkplaceDropdownMulti'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'
import { IDropdownOption } from '../../interfaces/IDropdownOptions'

type ModalRiskAssesmentProps = {
   setRiskManagementState: React.Dispatch<React.SetStateAction<IListState<RiskAnalysis>>>
   riskManagementState: IListState<RiskAnalysis>
   workplaces: Workplace[]
}
const useStyles = makeStyles({
   grid: {
      ...shorthands.margin(tokens.spacingHorizontalL, 0),
   },
   btnRight: { justifyContent: 'end' },
})
export const ModalRiskAssesment = ({ setRiskManagementState, riskManagementState, workplaces }: ModalRiskAssesmentProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount, organisation } = dataContext.state
   const [riskAnalysesTypeOptions, setRiskAnalysesTypeOptions] = useState<IDropdownOption[]>(getOptionsFromEnumValues(organisation))
   const navigate = useNavigate()
   const isWorkplaceDisabled = organisation.workplaceLimit === 0
   const defaultWorkplace = workplaces.find((wp) => wp.isDefault)
   const { open } = riskManagementState
   const {
      control,
      setValue,
      reset,
      getValues,
      handleSubmit,
      watch,
      formState: { isSubmitting, isValid, isSubmitSuccessful, isDirty },
   } = useForm<RiskAnalysis>({
      defaultValues: {
         analysisOwner: userAccount,
         analysisOwnerId: userAccount.id,
         workplaces: defaultWorkplace ? [defaultWorkplace] : [],
         dataClassification: DataClassification.InternalInformation,
      },
   })
   const [error, setError] = useState<string>(null)
   const classes = useStyles()

   useEffect(() => {
      setValue('workplaces', defaultWorkplace ? [defaultWorkplace] : [])
   }, [defaultWorkplace, isWorkplaceDisabled, setValue, workplaces])

   useEffect(() => {
      setRiskAnalysesTypeOptions(getOptionsFromEnumValues(organisation))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [organisation, organisation.modules])

   const onSubmit = async (data: RiskAnalysis) => {
      await addRiskAnalysis(setRiskManagementState, setError, reset, dataContext, data)
   }
   const openItem = () => {
      navigate(`/risk-management/${getValues().id}`)
      reset()
   }

   const closeAndReset = () => {
      reset({
         analysisOwner: userAccount,
         analysisOwnerId: userAccount.id,
         workplaces: defaultWorkplace ? [defaultWorkplace] : [],
         dataClassification: DataClassification.InternalInformation,
      })
      setRiskManagementState((prev) => ({ ...prev, open: false }))
   }

   return (
      <>
         <DraggableModal open={open} title={t('NewRiskAssesment')} onClose={closeAndReset}>
            <Form onSubmit={handleSubmit(onSubmit)}>
               <Grid className={classes.grid}>
                  <GridItem size="12/12">
                     <ControlledDropDown required control={control} name={'type'} label={t('TypeOfRiskAssesment')} options={riskAnalysesTypeOptions} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('NameOfAssesment')} control={control} name={'name'} required />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('Purpose/Scope')} control={control} name={'purposeScope'} required multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('Limitations')} control={control} name={'limitation'} required multiline />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledWorkplaceDropdownMulti
                        required
                        control={control}
                        name={'workplaces'}
                        label={t('Workplace')}
                        options={workplaces}
                        disabled={isWorkplaceDisabled}
                        showTooltip={isWorkplaceDisabled}
                        tooltipMsg={t('FeatureRequiresUpgrade')}
                     />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledDropDown
                        required
                        control={control}
                        name={'dataClassification'}
                        label={t('InformationManagement')}
                        options={enumValuesToOptions(DataClassification)}
                     />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledAccountPicker control={control} name={'analysisOwner'} label={t('RiskOwner')} watch={watch} setValue={setValue} required />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledAccountPicker control={control} name={'analysisLeader'} label={t('AnalysisLeader')} watch={watch} setValue={setValue} />
                  </GridItem>

                  <GridItem size="6/12">
                     <ControlledDatePicker control={control} name={'startDate'} label={t('StartOfAssesment')} />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledDatePicker control={control} name={'endDate'} label={t('EndOfAssesment')} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput control={control} name={'comment'} label={t('Comment')} multiline />
                  </GridItem>
               </Grid>
               <ModalActions>
                  <Button onClick={closeAndReset} disabled={isSubmitting}>
                     {t('Cancel')}
                  </Button>
                  <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                     {t('CreateAssesment')}
                  </Button>
               </ModalActions>
            </Form>
         </DraggableModal>

         <DraggableModal
            title={isSubmitting ? t('CreatingRiskAssesment') : error ? t('CouldNotCreateItem', { Item: t('RiskAssesment') }) : t('RiskAssesmentCreated')}
            open={isValid && (isSubmitting || isSubmitSuccessful)}
            noDivider
            noCloseButton
         >
            {isSubmitting ? (
               <>
                  <ProgressBar />
                  <Text>{t('ThisCanTakeAMoment')}</Text>
               </>
            ) : error ? (
               <Text>
                  {t('ErrorMessage')}: {error}
               </Text>
            ) : (
               <Text>{t('ClickContinueToGoToAssessment')}</Text>
            )}
            <ModalActions className={classes.btnRight}>
               {error ? (
                  <Button onClick={() => reset()} disabled={isSubmitting}>
                     {t('Close')}
                  </Button>
               ) : isSubmitSuccessful ? (
                  <Button appearance="primary" onClick={openItem}>
                     {t('Continue')}
                  </Button>
               ) : null}
            </ModalActions>
         </DraggableModal>
      </>
   )
}
